/**
 * Metpure Interactive FAQ Page Controller
 * Handles live search filtering, category tabs, accordion state, and deep linking
 */
class MetpureFaq {
  constructor(container) {
    this.container = container;
    this.searchInput = container.querySelector('.faq-metpure__search-input');
    this.searchClearBtn = container.querySelector('.faq-metpure__search-clear');
    this.searchStatus = container.querySelector('.faq-metpure__search-status');
    this.navButtons = container.querySelectorAll('.faq-metpure__nav-btn');
    this.groups = container.querySelectorAll('.faq-metpure__group');
    this.items = container.querySelectorAll('.faq-metpure__item');
    this.emptyState = container.querySelector('.faq-metpure__empty');
    this.emptyResetBtn = container.querySelector('.faq-metpure__empty-reset');
    this.expandToggleBtn = container.querySelector('.faq-metpure__expand-toggle');
    this.showingText = container.querySelector('.faq-metpure__showing-text');

    this.currentCategory = 'all';
    this.currentQuery = '';
    this.allExpanded = false;

    this.init();
  }

  init() {
    this.bindEvents();
    this.handleDeepLink();
    this.updateStats();
  }

  bindEvents() {
    // Accordion click & keyboard toggle
    this.items.forEach((item) => {
      const btn = item.querySelector('.faq-metpure__question');
      if (!btn) return;

      btn.addEventListener('click', () => {
        this.toggleItem(item);
      });
    });

    // Category navigation tabs
    this.navButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const category = btn.dataset.category;
        this.setCategory(category);
      });
    });

    // Live search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        this.handleSearch(e.target.value);
      });

      if (this.searchClearBtn) {
        this.searchClearBtn.addEventListener('click', () => {
          this.searchInput.value = '';
          this.handleSearch('');
          this.searchInput.focus();
        });
      }
    }

    // Reset button in empty state
    if (this.emptyResetBtn) {
      this.emptyResetBtn.addEventListener('click', () => {
        if (this.searchInput) this.searchInput.value = '';
        this.setCategory('all');
        this.handleSearch('');
      });
    }

    // Expand / Collapse all toggle
    if (this.expandToggleBtn) {
      this.expandToggleBtn.addEventListener('click', () => {
        this.toggleAll();
      });
    }
  }

  toggleItem(item, forceState = null) {
    const btn = item.querySelector('.faq-metpure__question');
    const answerWrap = item.querySelector('.faq-metpure__answer-wrapper');
    const isCurrentlyOpen = item.classList.contains('is-open');
    const shouldOpen = forceState !== null ? forceState : !isCurrentlyOpen;

    if (shouldOpen) {
      item.classList.add('is-open');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      if (answerWrap) answerWrap.removeAttribute('hidden');
    } else {
      item.classList.remove('is-open');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    }
  }

  toggleAll() {
    this.allExpanded = !this.allExpanded;
    const visibleItems = Array.from(this.items).filter(
      (item) => !item.classList.contains('is-hidden')
    );

    visibleItems.forEach((item) => {
      this.toggleItem(item, this.allExpanded);
    });

    if (this.expandToggleBtn) {
      this.expandToggleBtn.textContent = this.allExpanded
        ? 'Collapse All'
        : 'Expand All';
    }
  }

  setCategory(category) {
    this.currentCategory = category;

    // Update tab button classes
    this.navButtons.forEach((btn) => {
      if (btn.dataset.category === category) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });

    this.filterItems();
  }

  handleSearch(query) {
    this.currentQuery = query.trim().toLowerCase();

    // Show/hide clear button
    if (this.searchClearBtn) {
      this.searchClearBtn.style.display = this.currentQuery.length > 0 ? 'inline-flex' : 'none';
    }

    this.filterItems();
  }

  filterItems() {
    let matchCount = 0;

    this.items.forEach((item) => {
      const itemCategory = item.dataset.category || '';
      const questionEl = item.querySelector('.faq-metpure__question-text');
      const answerEl = item.querySelector('.faq-metpure__answer');

      const questionText = (questionEl ? questionEl.textContent : '').toLowerCase();
      const answerText = (answerEl ? answerEl.textContent : '').toLowerCase();

      const matchesCategory =
        this.currentCategory === 'all' || itemCategory === this.currentCategory;
      const matchesSearch =
        !this.currentQuery ||
        questionText.includes(this.currentQuery) ||
        answerText.includes(this.currentQuery);

      if (matchesCategory && matchesSearch) {
        item.classList.remove('is-hidden');
        matchCount++;
      } else {
        item.classList.add('is-hidden');
      }
    });

    // Handle Category groups visibility
    this.groups.forEach((group) => {
      const groupCategory = group.dataset.category;
      const visibleGroupItems = group.querySelectorAll('.faq-metpure__item:not(.is-hidden)');

      if (
        (this.currentCategory === 'all' || groupCategory === this.currentCategory) &&
        visibleGroupItems.length > 0
      ) {
        group.classList.remove('is-hidden');
      } else {
        group.classList.add('is-hidden');
      }
    });

    // Update empty state
    if (this.emptyState) {
      if (matchCount === 0) {
        this.emptyState.classList.add('is-visible');
      } else {
        this.emptyState.classList.remove('is-visible');
      }
    }

    // Update status text
    if (this.searchStatus) {
      if (this.currentQuery.length > 0) {
        this.searchStatus.textContent = `Found ${matchCount} question${matchCount === 1 ? '' : 's'} matching "${this.currentQuery}"`;
      } else {
        this.searchStatus.textContent = '';
      }
    }

    if (this.showingText) {
      this.showingText.textContent = `Showing ${matchCount} question${matchCount === 1 ? '' : 's'}`;
    }
  }

  updateStats() {
    // Populate pill count numbers
    this.navButtons.forEach((btn) => {
      const cat = btn.dataset.category;
      const countEl = btn.querySelector('.faq-metpure__nav-count');
      if (!countEl) return;

      if (cat === 'all') {
        countEl.textContent = this.items.length;
      } else {
        const count = this.container.querySelectorAll(`.faq-metpure__item[data-category="${cat}"]`).length;
        countEl.textContent = count;
      }
    });

    if (this.showingText) {
      this.showingText.textContent = `Showing ${this.items.length} questions`;
    }
  }

  handleDeepLink() {
    const hash = window.location.hash;
    if (!hash) return;

    try {
      const targetItem = this.container.querySelector(hash);
      if (targetItem && targetItem.classList.contains('faq-metpure__item')) {
        const itemCategory = targetItem.dataset.category;
        if (itemCategory) {
          this.setCategory('all'); // Show all so it's visible
        }
        this.toggleItem(targetItem, true);

        setTimeout(() => {
          targetItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    } catch (e) {
      // Invalid selector in hash, safely ignore
    }
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const faqSections = document.querySelectorAll('.faq-metpure');
  faqSections.forEach((section) => {
    new MetpureFaq(section);
  });
});

// Shopify Theme Editor Section Lifecycle Handlers
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.faq-metpure');
    if (section) {
      new MetpureFaq(section);
    }
  });
}
